/**
 * PH-62 — WHAT THE GITLAB SECTION SHOULD OFFER, as pure logic.
 *
 * 🔴 THE DEFECT: `POST /tickets/:id/gitlab/branch` and `/gitlab/merge-request` have both existed and
 *   worked since PH-8, and NOTHING IN THE PRODUCT CALLS EITHER. Two thirds of PH-8's title were
 *   unreachable from the UI. The issue path is live and proven (PH-29 created GitLab issue #1); these
 *   two were simply never given a trigger.
 *
 * ⛔⛔ THE RULE THIS ENCODES, AND IT IS THE TICKET'S OWN: THE BUTTON IS ABSENT, NOT DISABLED-AND-SILENT,
 *   WHEN THE PROJECT IS NOT LINKED. A disabled button is a promise the product cannot keep — it says
 *   "this exists for you" and gives no way to find out why it does not work. ⭐ 8 of 9 projects have
 *   `gitlab_project_id = null`, so unlinked is the COMMON case, not the edge case: getting this wrong
 *   would put dead buttons on almost every ticket in the system.
 *
 * ⚠️ AND THE ORDER IS A DEPENDENCY, NOT A PREFERENCE. GitLab needs a branch before a merge request can
 *   target one, and the API's `ensureDraftMergeRequest` will create the branch itself — but a UI that
 *   offers "Open draft MR" first invites a reader to think the branch is optional. Offering them in
 *   order makes the dependency visible without enforcing it.
 */
import type { Id } from '../../api/types';

/** What the API's `GET /tickets/:id/gitlab` tells us. All fields may be absent. */
export interface GitlabCodeState {
  issueIid: string | null;
  branchName: string | null;
  mergeRequestIid: string | null;
  /**
   * ⚠️ FALSE means GitLab was UNREACHABLE, not that nothing exists. The API answers 200 with this flag
   *   rather than 503, because a slow dependency must not black out a ticket page.
   */
  gitlabAvailable: boolean;
}

export type CodeAction = 'branch' | 'merge_request';

export interface CodeActionsView {
  /** ⛔ When false, the whole section is absent — not greyed out. */
  readonly show: boolean;
  /** Why the section is absent, for the ONE place that should say it: the project settings, not a tooltip. */
  readonly hiddenReason: 'project_not_linked' | null;
  readonly offered: readonly CodeAction[];
  /** Already done, so shown as a fact rather than an action. */
  readonly existing: readonly CodeAction[];
  /**
   * ⚠️ Set when GitLab could not be reached. The section still renders what WE know (our own
   * identifiers are ours and stay correct) but must not claim anything is absent.
   */
  readonly staleWarning: string | null;
}

export function codeActionsFor(
  state: GitlabCodeState | null,
  projectGitlabId: Id | null,
): CodeActionsView {
  /**
   * ⭐ The project link is checked FIRST and independently of `state`. A ticket in an unlinked project
   *   has no GitLab anything, and asking the API about it would return a 409 that the UI would then
   *   have to translate back into "not linked" — a round trip to learn what we already knew.
   */
  if (projectGitlabId === null) {
    return {
      show: false,
      hiddenReason: 'project_not_linked',
      offered: [],
      existing: [],
      staleWarning: null,
    };
  }

  if (state === null) {
    // Not loaded yet. ⛔ Offer nothing rather than guessing: a button that appears and then vanishes
    // once the real state arrives is worse than one that appears a moment late.
    return { show: true, hiddenReason: null, offered: [], existing: [], staleWarning: null };
  }

  const existing: CodeAction[] = [];
  if (state.branchName) existing.push('branch');
  if (state.mergeRequestIid) existing.push('merge_request');

  /**
   * ⛔ WHEN GITLAB IS UNREACHABLE, OFFER NOTHING — but keep the section and say why.
   *   ⭐⭐ This is the case that would otherwise do real damage: `branchName` is null both when no
   *     branch exists AND when we could not ask. Offering "Create branch" on the second reading sends
   *     a write at a GitLab we cannot see, and the honest answer is "we do not know yet".
   */
  if (!state.gitlabAvailable) {
    return {
      show: true,
      hiddenReason: null,
      offered: [],
      existing,
      staleWarning:
        'GitLab could not be reached, so this may be out of date. Anything shown is from PlanHub’s own records; nothing is claimed to be absent.',
    };
  }

  // ⭐ Order is the dependency: branch, then merge request. Each is offered only if not already done.
  const offered: CodeAction[] = [];
  if (!state.branchName) offered.push('branch');
  if (!state.mergeRequestIid) offered.push('merge_request');

  return { show: true, hiddenReason: null, offered, existing, staleWarning: null };
}

/** The API path for an action. ⭐ One place, so the two callers cannot disagree about the route. */
export function pathFor(ticketId: Id, action: CodeAction): string {
  return `/tickets/${ticketId}/gitlab/${action === 'branch' ? 'branch' : 'merge-request'}`;
}

/**
 * ⚠️ `merge-request` is HYPHENATED on the wire and `merge_request` is snake_case in our type. That
 *   mismatch is real and is the kind of thing a refactor silently "tidies" into a 404, so it lives in
 *   `pathFor` with a test pinning it rather than being spelled inline at each call site.
 */
export function labelFor(action: CodeAction): string {
  return action === 'branch' ? 'Create branch' : 'Open draft merge request';
}
